import React, {
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';
import { Alert, Button, Divider, message, Select } from 'antd';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import useSwr from '@hooks/useSwr';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import ActionTypes from '@utils/deviceActions';
import * as S from '@components/Actions/ActionsItems/Common/useCancellation/styles';
import CustomTag from '@components/CustomTag';
import { formatCurrency } from '@utils/formatters';
import SelectCustomers from '@components/SelectCustomers';
import useUpload from './Upload';
import { IContract } from '../types';

interface CancellationPenaltyPayloadProps {
  assetNumberParent: string;
  cancelAllChildren: boolean; // true = total, false = partial
  msisdnList?: string[];
}

interface CancellationPenaltyResponse {
  protocol: string;
  penalty?: {
    loyaltyStartDate: string; // '01-01-2021';
    loyaltyEndDate: string; // '31-12-2021';
    calculationBaseDate: string; // '09-06-2021';
    penaltyAmount: number;
  };
}

interface CancellationPayloadProps {
  protocol: string;
  cancellationType?: string;
}

type PenaltyRequestState = 'idle' | 'success' | 'asset_not_found' | 'error';

interface CancellationStepProps {
  currentStep: number;
  devicesProps?: any[];
}

const useCancellation = ({
  currentStep,
  devicesProps,
}: CancellationStepProps) => {
  const {
    selectedApp,
    isRoot,
    customer: { customerLogged },
  } = useContext(SessionContext);

  const [selectedCustomerId, setSelectedCustomerId] = useState<string>();

  const hasSelectedDevices = useMemo(
    () => devicesProps && devicesProps.length > 0,
    [devicesProps],
  );

  const deviceCustomerId = useMemo(
    () => (hasSelectedDevices ? devicesProps?.[0]?.customerId : undefined),
    [hasSelectedDevices, devicesProps],
  );

  const deviceAssetNumber = useMemo(
    () =>
      hasSelectedDevices ? devicesProps?.[0]?.contractAssetNumber : undefined,
    [hasSelectedDevices, devicesProps],
  );

  const customerId = useMemo(() => {
    if (hasSelectedDevices) return deviceCustomerId;
    return isRoot ? selectedCustomerId : customerLogged?.id;
  }, [
    customerLogged?.id,
    deviceCustomerId,
    hasSelectedDevices,
    isRoot,
    selectedCustomerId,
  ]);

  const cancellationType = 'partial' as string;
  const [assetNumberParent, setAssetNumberParent] = useState<string>();
  const [uploadedDevices, setUploadedDevices] =
    useState<{ msisdn: string }[]>();
  const [isRequestingPenalty, setIsRequestingPenalty] = useState(false);
  const [penaltyResponse, setPenaltyResponse] =
    useState<CancellationPenaltyResponse>();
  const [penaltyRequestState, setPenaltyRequestState] =
    useState<PenaltyRequestState>('idle');

  const { t } = useTranslation(['actions-modal']);

  const resetPenaltyState = useCallback(() => {
    setPenaltyResponse(undefined);
    setPenaltyRequestState('idle');
  }, []);

  const { uploadStep, uploadReinitState } = useUpload({
    actionKey: ActionTypes.CANCELLATION,
    setUploadedDevices: (devices: { msisdn: string }[]) => {
      setUploadedDevices(devices);
      resetPenaltyState();
    },
  });

  const consultPenalty = useCallback(
    (payload: CancellationPenaltyPayloadProps) =>
      api.post('/service-proxy/service-request/retention/penalty', payload),
    [],
  );

  const sendAction = useCallback(
    (payload: CancellationPayloadProps) =>
      api.post('/service-proxy/service-request/retention/acceptance', payload),
    [],
  );

  const { data: contracts } = useSwr<IContract[] | ''>(
    customerId ? '/service-proxy/contracts' : null,
    {
      params: {
        customerId,
        product: selectedApp?.toUpperCase(),
      },
    },
  );

  const effectiveAssetNumber = hasSelectedDevices
    ? deviceAssetNumber
    : assetNumberParent;

  const contractOptions = useMemo(
    () =>
      Array.isArray(contracts)
        ? contracts
            .filter(item => item.flagIsca !== '1')
            .map(({ planName, assetNumber, nickname }) => ({
              value: assetNumber,
              label: `${assetNumber} ${
                nickname ? ` - ${nickname}` : ''
              } - ${planName}`,
            }))
        : [],
    [contracts],
  );

  const handleRequestingPenalty = useCallback(async () => {
    setIsRequestingPenalty(true);
    resetPenaltyState();

    try {
      const penaltyPayload: CancellationPenaltyPayloadProps = {
        assetNumberParent: effectiveAssetNumber as string,
        cancelAllChildren: cancellationType === 'total',
      };

      if (cancellationType === 'partial') {
        penaltyPayload.msisdnList = hasSelectedDevices
          ? devicesProps?.map((item: any) => item.msisdn)
          : uploadedDevices?.map(item => item.msisdn) ?? [];
      }
      const { data } = await consultPenalty(penaltyPayload);
      setPenaltyResponse(data);
      setPenaltyRequestState('success');
    } catch (error) {
      const responseData = (error as AxiosError<any>)?.response?.data;
      if (responseData?.message === 'ASSET_NOT_FOUND_IN_RMS') {
        setPenaltyRequestState('asset_not_found');
      } else {
        setPenaltyRequestState('error');
      }
      setPenaltyResponse(undefined);
    }

    setTimeout(() => {
      setIsRequestingPenalty(false);
    }, 1000);
  }, [
    effectiveAssetNumber,
    cancellationType,
    consultPenalty,
    resetPenaltyState,
    uploadedDevices,
    hasSelectedDevices,
    devicesProps,
  ]);

  // const handleChangeCancellationType = useCallback(
  //   (value: string) => {
  //     setCancellationType(value);
  //     resetPenaltyState();
  //   },
  //   [resetPenaltyState],
  // );

  const handleChangeAssetNumberParent = useCallback(
    (value: string) => {
      setAssetNumberParent(value);
      resetPenaltyState();
    },
    [resetPenaltyState],
  );

  const formatLoyaltyDate = (date: any) => date?.replace(/-/g, '/');

  const penaltyStepContent = useMemo(() => {
    let stepContent = (
      <>
        <p>{t('cancellation.consultPenaltyDescription')}</p>

        <Button
          type="primary"
          onClick={handleRequestingPenalty}
          disabled={
            !effectiveAssetNumber ||
            (cancellationType === 'partial' &&
              !hasSelectedDevices &&
              !uploadedDevices?.length)
          }
          loading={isRequestingPenalty}
        >
          {t(
            `cancellation.${
              isRequestingPenalty ? 'consultingPenalty' : 'consultPenalty'
            }`,
          )}
        </Button>
      </>
    );

    const isPenaltySuccessState = penaltyRequestState === 'success';
    const isPenaltyErrorState = penaltyRequestState === 'error';
    const isPenaltyAssetNotFoundState =
      penaltyRequestState === 'asset_not_found';

    if (isPenaltySuccessState) {
      const hasPenalty = !!penaltyResponse?.penalty;
      stepContent = hasPenalty ? (
        <S.CancellationPenaltyContent>
          <p>{t('cancellation.estimatedPenalty')}</p>

          <CustomTag className="cancellation-penalty-value">
            {formatCurrency(penaltyResponse?.penalty?.penaltyAmount)}
          </CustomTag>

          {cancellationType === 'total' && (
            <>
              <Divider />
              <p className="cancellation-penalty-loyalty">
                {`${t('cancellation.contractLoyalty')}: `}
                <strong>
                  {`${formatLoyaltyDate(
                    penaltyResponse?.penalty?.loyaltyStartDate,
                  )} - ${formatLoyaltyDate(
                    penaltyResponse?.penalty?.loyaltyEndDate,
                  )}`}
                </strong>
              </p>
            </>
          )}
        </S.CancellationPenaltyContent>
      ) : (
        <div className="cancellation-penalty-not-found">
          <p>{t('cancellation.penaltyNotFound')}</p>
          <span>{t('cancellation.penaltyNotFoundDescription')}</span>
        </div>
      );
    } else if (isPenaltyAssetNotFoundState) {
      stepContent = <span>{t('cancellation.assetNotFound')}</span>;
    } else if (isPenaltyErrorState) {
      stepContent = <span>{t('cancellation.penaltyConsultationError')}</span>;
    }

    return (
      <S.CancellationPenaltyContainer>
        <Alert
          description={t('cancellation.simcardDescription')}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        {cancellationType === 'total' && (
          <Alert
            description={t('cancellation.totalPenaltyAlert')}
            type="warning"
            showIcon
            style={{ marginBottom: 16 }}
          />
        )}
        {stepContent}
      </S.CancellationPenaltyContainer>
    );
  }, [
    effectiveAssetNumber,
    cancellationType,
    handleRequestingPenalty,
    hasSelectedDevices,
    isRequestingPenalty,
    penaltyRequestState,
    penaltyResponse?.penalty,
    t,
    uploadedDevices?.length,
  ]);

  const contentSteps = useMemo(() => {
    const defaultSteps: { title: ReactNode; content: ReactNode }[] = [
      // {
      //   title: (
      //     <>
      //       <span>{t('cancellation.type')}</span>
      //       <Popover
      //         content={
      //           <S.CancellationTypeDescription>
      //             <p>
      //               <strong>{`${t('cancellation.partial')}: `}</strong>
      //               {t('cancellation.partialDescription')}
      //             </p>
      //             <p>
      //               <strong>{`${t('cancellation.total')}: `}</strong>
      //               {t('cancellation.totalDescription')}
      //             </p>
      //           </S.CancellationTypeDescription>
      //         }
      //       >
      //         <Button type="link" size="small" icon={<InfoCircleOutlined />} />
      //       </Popover>
      //     </>
      //   ),
      //   content: (
      //     <div
      //       style={{
      //         padding: 16,
      //         display: 'flex',
      //         justifyContent: 'center',
      //       }}
      //     >
      //       <Radio.Group
      //         value={cancellationType}
      //         onChange={event => {
      //           handleChangeCancellationType(event.target.value);
      //         }}
      //       >
      //         <Radio.Button value="partial">
      //           {t('cancellation.partial')}
      //         </Radio.Button>
      //         <Radio.Button value="total" disabled={!!hasSelectedDevices}>
      //           {t('cancellation.total')}
      //         </Radio.Button>
      //       </Radio.Group>
      //     </div>
      //   ),
      // },
    ];

    if (isRoot && !hasSelectedDevices) {
      defaultSteps.push({
        title: t('cancellation.client'),
        content: (
          <div
            style={{
              padding: 16,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <SelectCustomers
              value={selectedCustomerId}
              onChange={(value: string) => {
                setSelectedCustomerId(value);
                setAssetNumberParent(undefined);
                resetPenaltyState();
              }}
              style={{ width: '100%' }}
            />
          </div>
        ),
      });
    }

    if (!hasSelectedDevices) {
      defaultSteps.push({
        title: t('cancellation.contract'),
        content: (
          <div
            style={{
              padding: 16,
              display: 'flex',
              justifyContent: 'center',
            }}
          >
            <Select
              value={assetNumberParent}
              options={contractOptions}
              onChange={handleChangeAssetNumberParent}
              style={{ width: '100%' }}
              disabled={!customerId || contracts === undefined}
              loading={!!customerId && contracts === undefined}
            />
          </div>
        ),
      });
    }

    if (cancellationType === 'partial' && !hasSelectedDevices) {
      defaultSteps.push(uploadStep);
    }

    defaultSteps.push({
      title: t('cancellation.penalty'),
      content: penaltyStepContent,
    });

    return defaultSteps;
  }, [
    assetNumberParent,
    contractOptions,
    contracts,
    customerId,
    handleChangeAssetNumberParent,
    hasSelectedDevices,
    isRoot,
    penaltyStepContent,
    resetPenaltyState,
    selectedCustomerId,
    t,
    uploadStep,
  ]);

  const reinitState = () => {
    uploadReinitState();
    setUploadedDevices([]);
    setAssetNumberParent(undefined);
    setSelectedCustomerId(undefined);
    resetPenaltyState();
  };

  const nextStepIsDisabled = useCallback(() => {
    const isPartial = cancellationType === 'partial';
    const hasDevices =
      hasSelectedDevices || (uploadedDevices && uploadedDevices.length > 0);

    let stepIndex = 0;
    if (isRoot && !hasSelectedDevices) {
      if (currentStep === stepIndex) return !selectedCustomerId;
      stepIndex += 1;
    }
    if (!hasSelectedDevices) {
      if (currentStep === stepIndex) return !assetNumberParent;
      stepIndex += 1;
    }
    if (isPartial && !hasSelectedDevices && currentStep === stepIndex)
      return !hasDevices;

    if (!cancellationType) return true;
    if (!effectiveAssetNumber) return true;
    if (isPartial && !hasDevices) return true;
    if (penaltyRequestState !== 'success') return true;

    return false;
  }, [
    cancellationType,
    uploadedDevices,
    currentStep,
    assetNumberParent,
    effectiveAssetNumber,
    penaltyRequestState,
    hasSelectedDevices,
    isRoot,
    selectedCustomerId,
  ]);

  const onSubmit = useCallback(async () => {
    try {
      const cancellationPayload: CancellationPayloadProps = {
        protocol: penaltyResponse?.protocol || '',
        cancellationType,
      };
      await sendAction(cancellationPayload);
    } catch {
      message.error(t('cancellation.penaltyConsultationError'));
    }
  }, [cancellationType, penaltyResponse?.protocol, sendAction, t]);

  const cancellationData = {
    contentSteps,
    onSubmit,
    reinitState,
    nextStepIsDisabled,
    confirmDisabled: !penaltyResponse?.protocol,
  };

  return { cancellationData };
};

export default useCancellation;
